import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowPathIcon,
  BoltIcon,
  StopIcon,
  TrashIcon,
  PlusCircleIcon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import WhatsAppConnectionModal from '../components/WhatsAppConnectionModal';
import WhatsAppInstanceDetailModal from '../components/WhatsAppInstanceDetailModal';
import WhatsAppWebhookModal from '../components/WhatsAppWebhookModal';
import { cn } from '../lib/utils';

const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/$/, '');


function formatBrl(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
}

function billingPriceLabel(billing) {
  if (billing?.price == null) return null;
  const cycle = billing.billingCycleLabel || 'mês';
  return `${formatBrl(billing.price)}/${cycle}`;
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function normalizeRole(role) {
  if (!role) return 'client';
  const r = String(role).toLowerCase();
  if (r === 'admin' || r === 'super_admin') return 'super_admin';
  if (r === 'cliente' || r === 'client') return 'client';
  if (r === 'white_label_admin') return 'white_label_admin';
  return r;
}

/** Cores alinhadas aos tokens do tema (primary, muted, destructive). */
function statusPresentation(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'connected') {
    return {
      label: 'Conectado',
      borderAccent: 'border-l-primary',
      badgeVariant: 'default'
    };
  }
  if (s === 'waiting_connection' || s === 'connecting') {
    return {
      label: 'Aguardando QR',
      borderAccent: 'border-l-muted-foreground',
      badgeVariant: 'secondary'
    };
  }
  if (s === 'disconnected') {
    return {
      label: 'Desconectado',
      borderAccent: 'border-l-muted-foreground/60',
      badgeVariant: 'outline'
    };
  }
  if (s === 'error') {
    return {
      label: 'Erro',
      borderAccent: 'border-l-destructive',
      badgeVariant: 'destructive'
    };
  }
  return {
    label: String(status || '—'),
    borderAccent: 'border-l-border',
    badgeVariant: 'outline'
  };
}

export default function WhatsAppInstancias() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const effRole = normalizeRole(userData.role);

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novaNome, setNovaNome] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pedidosPendentes, setPedidosPendentes] = useState([]);
  const [pedidosLoading, setPedidosLoading] = useState(false);
  const [syncPayLoadingId, setSyncPayLoadingId] = useState(null);
  const [deleteOrderLoadingId, setDeleteOrderLoadingId] = useState(null);
  const [connectionModal, setConnectionModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [webhookModal, setWebhookModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailActionLoading, setDetailActionLoading] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (effRole !== 'client') return;
    axios
      .get(`${baseUrl}/api/instance-orders/billing-info`, authHeaders())
      .then((res) => setBilling(res.data))
      .catch(() => setBilling({ price: 10, billingCycleLabel: 'mensal', chargeType: 'subscription' }));
  }, [effRole]);

  const priceLabel = billingPriceLabel(billing);

  const carregar = useCallback(async () => {
    if (effRole !== 'client') {
      setLoading(false);
      setLista([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/api/whatsapp-instances`, authHeaders());
      setLista(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao carregar instâncias');
    } finally {
      setLoading(false);
    }
  }, [effRole]);

  const carregarPedidosPendentes = useCallback(async () => {
    if (effRole !== 'client') return;
    try {
      setPedidosLoading(true);
      const res = await axios.get(`${baseUrl}/api/instance-orders`, {
        ...authHeaders(),
        params: { status: 'pending' }
      });
      setPedidosPendentes(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao carregar pedidos');
    } finally {
      setPedidosLoading(false);
    }
  }, [effRole]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    carregarPedidosPendentes();
  }, [carregarPedidosPendentes]);

  const checkout = async () => {
    if (!novaNome.trim()) {
      toast.error('Informe um nome para a instância');
      return;
    }
    try {
      setCheckoutLoading(true);
      const res = await axios.post(
        `${baseUrl}/api/instance-orders/checkout`,
        { displayName: novaNome.trim() },
        authHeaders()
      );
      if (res.data.paymentMethod === 'credits') {
        toast.success('Instância liberada com créditos.');
        setNovaNome('');
        carregar();
        carregarPedidosPendentes();
        return;
      }
      const url = res.data.paymentUrl;
      const oid = res.data.order?._id;
      if (oid) {
        try {
          sessionStorage.setItem('lastWhatsappCheckoutOrderId', oid);
        } catch {
          /* ignore */
        }
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success('Abra o link de pagamento. Depois use "Já paguei — verificar" abaixo.');
      } else {
        toast.success('Cobrança criada. Use "Já paguei — verificar" ou aguarde o webhook.');
      }
      setNovaNome('');
      carregar();
      carregarPedidosPendentes();
    } catch (e) {
      const d = e.response?.data;
      const msg =
        (typeof d?.message === 'string' && d.message) ||
        (Array.isArray(d?.errors) && d.errors[0]?.description) ||
        d?.code ||
        'Erro ao iniciar compra';
      toast.error(msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const syncPaymentAsaas = async (orderId) => {
    if (!orderId) return;
    try {
      setSyncPayLoadingId(orderId);
      const res = await axios.post(
        `${baseUrl}/api/instance-orders/sync-payment`,
        { orderId },
        authHeaders()
      );
      const d = res.data;
      if (d.skipped && d.reason === 'payment_not_confirmed_yet') {
        toast(`Asaas ainda não marcou como pago (status: ${d.asaasStatus || '—'}). Tente de novo em instantes.`);
        return;
      }
      if (d.skipped && d.reason === 'not_asaas_order') {
        toast.error('Este pedido não é cobrança Asaas.');
        return;
      }
      if (d.error === 'uazapi_init' || d.error === 'no_token') {
        toast.error(d.message || 'Pagamento ok, mas falhou ao criar instância. Contate o suporte.');
        carregarPedidosPendentes();
        return;
      }
      if (d.instance) {
        toast.success('Instância criada.');
        carregar();
        carregarPedidosPendentes();
        return;
      }
      if (d.skipped && d.reason === 'already_fulfilled') {
        toast.success('Instância já estava criada.');
        carregar();
        carregarPedidosPendentes();
        return;
      }
      toast.success('Sincronizado.');
      carregar();
      carregarPedidosPendentes();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Erro ao verificar pagamento';
      toast.error(msg);
    } finally {
      setSyncPayLoadingId(null);
    }
  };

  const apagarPedidoPendente = async (orderId) => {
    if (!orderId) return;
    if (
      !window.confirm(
        'Remover este pedido pendente da lista? A cobrança no Asaas (se existir) não é cancelada automaticamente — cancele no painel Asaas se precisar.'
      )
    ) {
      return;
    }
    try {
      setDeleteOrderLoadingId(orderId);
      await axios.post(
        `${baseUrl}/api/instance-orders/cancel-pending`,
        { orderId },
        authHeaders()
      );
      toast.success('Pedido removido da lista.');
      carregarPedidosPendentes();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao remover pedido');
    } finally {
      setDeleteOrderLoadingId(null);
    }
  };

  const sync = async (id) => {
    try {
      const res = await axios.post(`${baseUrl}/api/whatsapp-instances/${id}/sync`, {}, authHeaders());
      toast.success(`Status: ${res.data.status}`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao sincronizar');
    }
  };

  const openConnectionUi = (inst) => {
    setConnectionModal({
      id: inst._id,
      name: inst.name,
      initialQr: inst.connectionQrCode || null,
      initialPairing: inst.connectionPairingCode || null
    });
  };

  const openDetail = async (inst) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/api/whatsapp-instances/${inst._id}/detail`, authHeaders());
      setDetailModal(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao carregar detalhes da instância');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailConnect = (inst) => {
    setDetailModal(null);
    openConnectionUi(inst);
  };

  const handleDetailDisconnect = async (inst) => {
    setDetailActionLoading(true);
    try {
      await disconnect(inst._id, inst.name);
      setDetailModal(null);
    } finally {
      setDetailActionLoading(false);
    }
  };

  const stopCardActionClick = (e) => e.stopPropagation();

  const disconnect = async (id, name) => {
    if (!window.confirm(`Desconectar o WhatsApp da instância "${name || ''}"?`)) return;
    try {
      await axios.post(`${baseUrl}/api/whatsapp-instances/${id}/disconnect`, {}, authHeaders());
      if (connectionModal?.id === id) setConnectionModal(null);
      toast.success('WhatsApp desconectado.');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao desconectar');
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta instância? Esta ação não pode ser desfeita.')) return;
    try {
      await axios.delete(`${baseUrl}/api/whatsapp-instances/${id}`, authHeaders());
      toast.success('Removida');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro');
    }
  };

  if (effRole !== 'client') {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-border bg-card px-8 py-12 text-center text-card-foreground shadow-sm">
        <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-primary" />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">WhatsApp</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A gestão de instâncias para o seu perfil está no menu &quot;WhatsApp (gestão)&quot;.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8">
      <WhatsAppWebhookModal
        open={!!webhookModal}
        instance={webhookModal}
        apiBaseUrl={baseUrl}
        onClose={() => setWebhookModal(null)}
      />
      <WhatsAppInstanceDetailModal
        open={!!detailModal}
        instance={detailModal}
        serverUrl={detailModal?.serverUrl}
        apiBaseUrl={baseUrl}
        onClose={() => setDetailModal(null)}
        onConnect={handleDetailConnect}
        onDisconnect={handleDetailDisconnect}
        onConfigureWebhook={(inst) => setWebhookModal(inst)}
        onVerifyPayment={syncPaymentAsaas}
        actionLoading={detailActionLoading || detailLoading}
        showToken
      />
      <WhatsAppConnectionModal
        open={!!connectionModal}
        instanceId={connectionModal?.id}
        instanceName={connectionModal?.name || ''}
        initialQr={connectionModal?.initialQr}
        initialPairing={connectionModal?.initialPairing}
        baseUrl={baseUrl}
        onClose={() => setConnectionModal(null)}
        onConnected={() => {
          carregar();
          setDetailModal(null);
        }}
        onDisconnected={() => {
          carregar();
          setDetailModal(null);
        }}
      />

      <header className="border-b border-border pb-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-primary">Canal WhatsApp</p>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Instâncias <span className="text-primary">em produção</span>
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              Assinatura {priceLabel ? <strong>{priceLabel}</strong> : 'mensal'} via Asaas — conecte o número com QR
              e mantenha o status sincronizado com a nossa API.
            </p>
          </div>
          <div className="hidden shrink-0 sm:flex">
            <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-border bg-card text-primary shadow-sm">
              <ChatBubbleLeftRightIcon className="h-10 w-10" />
            </div>
          </div>
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-5">
        <div className="space-y-8 lg:col-span-3">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/80 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-card-foreground">
                <PlusCircleIcon className="h-5 w-5 text-primary" />
                Nova instância
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Nome visível no painel · assinatura recorrente no Asaas
                {priceLabel ? ` (${priceLabel})` : ''}
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1 space-y-2">
                <Label htmlFor="nome-inst">Nome de exibição</Label>
                <Input
                  id="nome-inst"
                  value={novaNome}
                  onChange={(e) => setNovaNome(e.target.value)}
                  placeholder="Ex.: Atendimento vendas"
                />
              </div>
              <Button onClick={checkout} disabled={checkoutLoading} className="shrink-0 sm:h-10">
                {checkoutLoading ? 'Processando…' : priceLabel ? `Assinar (${priceLabel})` : 'Criar e pagar'}
              </Button>
            </CardContent>
          </Card>

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <SignalIcon className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Suas instâncias</h2>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={carregar} disabled={loading}>
                <ArrowPathIcon className={cn('h-4 w-4', loading && 'animate-spin')} />
                Atualizar
              </Button>
            </div>

            {loading ? (
              <Card className="border-dashed">
                <CardContent className="flex items-center gap-3 py-10 text-sm text-muted-foreground">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                  Carregando instâncias…
                </CardContent>
              </Card>
            ) : lista.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center text-sm text-muted-foreground">
                  Nenhuma instância ainda.
                  <p className="mt-2 text-xs">Crie acima ou conclua o pagamento pendente à direita.</p>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-4">
                {lista.map((inst) => {
                  const st = statusPresentation(inst.status);
                  return (
                    <li key={inst._id}>
                      <Card
                        className={cn(
                          'cursor-pointer overflow-hidden border-l-4 shadow-sm transition-colors hover:bg-accent/30',
                          st.borderAccent
                        )}
                        onClick={() => openDetail(inst)}
                      >
                        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="truncate text-base font-semibold text-card-foreground">{inst.name}</h3>
                              <Badge variant={st.badgeVariant}>{st.label}</Badge>
                            </div>
                            <p className="font-mono text-[11px] text-muted-foreground">
                              {inst.uazapiInstanceName}
                              {inst.phoneNumber ? (
                                <span className="text-foreground/80"> · {inst.phoneNumber}</span>
                              ) : null}
                            </p>
                            {inst.connectionQrCode || inst.connectionPairingCode ? (
                              <p className="text-xs text-primary">QR ou pareamento disponível — use Conectar.</p>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-2 sm:justify-end" onClick={stopCardActionClick}>
                            <Button type="button" variant="outline" size="sm" onClick={() => sync(inst._id)}>
                              <ArrowPathIcon className="h-4 w-4" />
                              Sync
                            </Button>
                            {inst.status !== 'connected' && (
                              <Button type="button" variant="default" size="sm" onClick={() => openConnectionUi(inst)}>
                                <BoltIcon className="h-4 w-4" />
                                Conectar
                              </Button>
                            )}
                            {(inst.status === 'connected' || inst.status === 'waiting_connection') && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => disconnect(inst._id, inst.name)}
                              >
                                <StopIcon className="h-4 w-4" />
                                Desconectar
                              </Button>
                            )}
                            <Button type="button" variant="destructive" size="sm" onClick={() => remover(inst._id)}>
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <aside className="lg:col-span-2">
          <Card className="sticky top-6 border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/80 pb-4">
              <CardTitle className="flex items-center gap-2 text-base text-card-foreground">
                <BanknotesIcon className="h-5 w-5 text-primary" />
                Pagamento pendente
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={carregarPedidosPendentes}
                disabled={pedidosLoading}
                className="h-9 w-9 p-0"
              >
                <ArrowPathIcon className={cn('h-4 w-4', pedidosLoading && 'animate-spin')} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Webhook em localhost não avisa o Asaas — use verificar após pagar. &quot;Apagar&quot; só cancela o
                pedido aqui (não estorna no Asaas).
              </p>
              {pedidosLoading ? (
                <p className="py-6 text-center text-xs text-muted-foreground">Carregando…</p>
              ) : pedidosPendentes.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-muted/30 py-8 text-center text-xs text-muted-foreground">
                  Nenhum pedido pendente.
                </p>
              ) : (
                <ul className="space-y-3">
                  {pedidosPendentes.map((p) => (
                    <li key={p._id} className="rounded-lg border border-border bg-muted/20 p-3">
                      <p className="font-medium text-card-foreground">{p.displayName || p.instanceName}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {p.amount != null && p.amount > 0
                          ? `${formatBrl(p.amount)}/${p.billingCycle === 'MONTHLY' ? 'mês' : 'período'} · `
                          : ''}
                        {p.paymentMethod === 'asaas' ? 'Asaas (assinatura)' : p.paymentMethod} ·{' '}
                        {p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="min-w-0 flex-1"
                          disabled={syncPayLoadingId === p._id || p.paymentMethod !== 'asaas'}
                          onClick={() => syncPaymentAsaas(p._id)}
                        >
                          {syncPayLoadingId === p._id ? 'Verificando…' : 'Já paguei — verificar'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          title="Remover pedido pendente"
                          aria-label="Apagar pedido pendente"
                          disabled={deleteOrderLoadingId === p._id}
                          onClick={() => apagarPedidoPendente(p._id)}
                          className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10"
                        >
                          {deleteOrderLoadingId === p._id ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}
